import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Package, MoreVertical, Share2, Printer, Clock, Search } from 'lucide-react';
import LotCard from './LotCard';
import MovementCard from './MovementCard';
import MovementFilters from './MovementFilters';
import LotFilters from './LotFilters';
import { useLots } from '../../../contexts/LotsContext';
import { SegmentedControl } from '../../../components/ui/SegmentedControl';

// Opciones para el SegmentedControl
const VIEW_OPTIONS = [
  { value: 'inventory', label: 'Disponible', className: 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white' },
  { value: 'movements', label: 'Movimientos', className: 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white' }
];

const LotsView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('inventory');
  const { lots } = useLots();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('available');
  const [movementFilters, setMovementFilters] = useState({
    add: true,
    remove: true,
    modify: true
  });
  const [showActions, setShowActions] = useState(false);

  // Manejar el filtro desde la navegación
  useEffect(() => {
    const handleFilterFromNavigation = () => {
      if (location.state?.filter) {
        console.log('Setting filter from navigation:', location.state.filter);
        setActiveFilter(location.state.filter);
        setActiveTab('inventory');
        
        // Limpiar el state después de un breve delay para asegurar que el filtro se aplique
        setTimeout(() => {
          navigate(location.pathname, { replace: true });
        }, 100);
      }
    };

    handleFilterFromNavigation();
  }, [location.state, navigate, location.pathname]);

  // Filter and sort lots
  const filteredLots = lots ? lots
    .filter(lot => {
      // Primero aplicar filtro de estado
      const matchesFilter = (() => {
        switch (activeFilter) {
          case 'lightning':
            return lot.status === 'lightning';
          case 'available':
            return lot.quantity > 0;
          case 'nostock':
            return lot.quantity === 0;
          case 'review':
            return lot.quantity < 0;
          case 'all':
          default:
            return true;
        }
      })();

      // Luego aplicar búsqueda
      const matchesSearch = search === '' || 
        lot.title?.toLowerCase().includes(search.toLowerCase()) ||
        lot.supplier?.toLowerCase().includes(search.toLowerCase()) ||
        lot.display_id?.toLowerCase().includes(search.toLowerCase()) ||
        lot.id?.toLowerCase().includes(search.toLowerCase());

      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    : [];

  // Get filtered movements
  const movements = lots ? lots
    .flatMap(lot => 
      (lot.movements || [])
        .filter(m => movementFilters[m.type])
        .filter(m => 
          lot.title?.toLowerCase().includes(search.toLowerCase()) ||
          lot.supplier?.toLowerCase().includes(search.toLowerCase()) ||
          m.note?.toLowerCase().includes(search.toLowerCase())
        )
        .map(m => ({
          lot,
          movement: m
        }))
    )
    .sort((a, b) => new Date(b.movement.timestamp) - new Date(a.movement.timestamp))
    : [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
        {/* Título y menú */}
        <div className="p-4 flex items-center justify-between">
          <div className="w-5"></div> {/* Spacer para equilibrar el botón de la derecha */}
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Inventario</h1>
          <div className="relative">
            <button 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-700 dark:text-gray-300"
              onClick={() => setShowActions(!showActions)}
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {showActions && (
              <>
                <div 
                  className="fixed inset-0 z-20"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-1 z-30">
                  <button className="w-full px-4 py-2 text-left flex items-center text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Share2 className="w-4 h-4 mr-2" />
                    Compartir listado
                  </button>
                  <button className="w-full px-4 py-2 text-left flex items-center text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimir listado
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          options={VIEW_OPTIONS}
        />
      </div>

      <div className="p-4 pt-0">
        {/* Barra de búsqueda */}
        <div className="mb-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600"
            />
          </div>
        </div>

        {/* Filtros y contenido */}
        {activeTab === 'inventory' ? (
          <LotFilters 
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            filteredLots={filteredLots}
          />
        ) : (
          <MovementFilters 
            filters={movementFilters} 
            setFilters={setMovementFilters}
            movements={movements}
          />
        )}
        
        <div className="space-y-4 mt-4">
          {activeTab === 'inventory' ? (
            filteredLots.map(lot => (
              <LotCard key={lot.id} lot={lot} />
            ))
          ) : (
            movements.map((m, idx) => (
              <MovementCard 
                key={`${m.lot.id}-${idx}`} 
                lot={m.lot}
                movement={m.movement}
              />
            ))
          )}

          {activeTab === 'inventory' && filteredLots.length === 0 && (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              No hay lotes disponibles
            </div>
          )}

          {activeTab === 'movements' && movements.length === 0 && (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              No hay movimientos registrados
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LotsView; 